import React from "react";
import { ExternalLink, ImageIcon, MapPin } from "lucide-react";

const HIGHLIGHT_CLASS = {
  hero: "inset-x-3 top-7 bottom-3",
  banner: "inset-x-5 top-16 h-8",
  tiles: "bottom-3 left-3 h-10 w-[42%]",
  gallery: "bottom-3 left-3 h-10 w-[42%]",
};

export const SiteMediaPlacementPreview = ({ placement, copy, lang, activeItems }) => {
  const current = activeItems[0] || (placement.fallbackImage ? {
    url: placement.fallbackImage,
    title: copy.currentDefault,
  } : null);
  const hasMore = activeItems.length > 1;
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <div>
        <div
          className="relative aspect-[16/9] overflow-hidden border border-black/15 bg-neutral-100"
          data-testid={`placement-wireframe-${placement.variant}`}
        >
          <div className="absolute inset-x-0 top-0 h-5 border-b border-black/10 bg-white" />
          <div className="absolute inset-x-3 top-9 h-8 bg-neutral-200" />
          <div className="absolute inset-x-3 bottom-3 grid grid-cols-2 gap-2">
            <div className="bg-neutral-200" />
            <div className="grid grid-rows-3 gap-1.5">
              <div className="bg-neutral-200" />
              <div className="bg-neutral-200" />
              <div className="bg-neutral-200" />
            </div>
          </div>
          <div
            className={`absolute border-2 border-lm-red bg-lm-red/10 ${HIGHLIGHT_CLASS[placement.variant]}`}
          >
            <span className="absolute -top-5 left-0 bg-lm-red px-1.5 py-0.5 text-[9px] font-bold text-white">
              {placement.section[lang]}
            </span>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-soft">
          <MapPin size={13} className="text-lm-red" /> {copy.placementPreview}
        </p>
      </div>
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-ink-soft">{copy.page}</p>
            <p className="mt-1 text-sm font-semibold">{placement.page[lang]}</p>
          </div>
          <a
            href={placement.viewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`view-placement-${placement.variant}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-lm-red hover:underline"
          >
            {copy.viewOnPage} <ExternalLink size={13} />
          </a>
        </div>
        <p className="mt-3 text-sm text-ink-soft">{placement.description[lang]}</p>
        <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
          <div>
            <dt className="uppercase tracking-widest text-ink-soft">{copy.recommendedSize}</dt>
            <dd className="mt-1 font-semibold">{placement.dimensions}</dd>
            {placement.cropNote && <dd className="mt-1 text-ink-soft">{placement.cropNote[lang]}</dd>}
          </div>
          <div>
            <dt className="uppercase tracking-widest text-ink-soft">{copy.acceptedFiles}</dt>
            <dd className="mt-1 font-semibold">{placement.accepted}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-ink-soft">
          <span className="font-semibold text-ink">{copy.recommendedUse}:</span> {placement.purpose[lang]}
        </p>
      </div>
      <div className="lg:col-span-2">
        <p className="mb-2 text-[11px] uppercase tracking-widest text-ink-soft">{copy.currentImages}</p>
        {current ? (
          <div className="flex items-center gap-3 border border-black/10 bg-neutral-50 p-2">
            <img
              src={current.url}
              alt={current.title || placement.label[lang]}
              className="h-14 w-20 object-cover"
              data-testid={`active-thumbnail-${placement.variant}`}
            />
            <p className="min-w-0 text-xs text-ink-soft">
              {current.title || current.original_filename || placement.label[lang]}
              {hasMore ? ` +${activeItems.length - 1}` : ""}
            </p>
          </div>
        ) : (
          <div
            data-testid={`empty-thumbnail-${placement.variant}`}
            className="flex items-center gap-2 border border-dashed border-black/20 bg-neutral-50 p-3 text-xs text-ink-soft"
          >
            <ImageIcon size={16} /> {copy.noActiveImage}
          </div>
        )}
      </div>
    </div>
  );
};